import apiClient from './client';

export interface AnalyzePromptRequest {
  prompt: string;
  /** URLs (already uploaded to S3 via uploadApi) of reference photos of the business's shop/products/menu, if any. */
  referenceImageUrls?: string[];
}

export interface CreateWebsiteRequest {
  businessName: string;
  category?: string;
  description?: string;
  logo?: string;
  selectedSections?: string[];
  prompt?: string;
}

export interface UpdateWebsiteRequest {
  businessName?: string;
  slug?: string;
  category?: string;
  description?: string;
  logo?: string;
  favicon?: string;
  metaTitle?: string;
  metaDescription?: string;
  metaKeywords?: string[];
  status?: 'draft' | 'published' | 'unpublished';
}

export const websiteApi = {
  analyzePrompt: async (data: AnalyzePromptRequest) => {
    const response = await apiClient.post('/business/websites/analyze-prompt', data);
    return response.data.data;
  },

  createWebsiteFromPrompt: async (data: AnalyzePromptRequest) => {
    const response = await apiClient.post('/business/websites/create-from-prompt', data);
    return response.data.data;
  },

  createWebsite: async (data: CreateWebsiteRequest) => {
    const response = await apiClient.post('/business/websites', data);
    return response.data.data;
  },

  getMyWebsites: async () => {
    const response = await apiClient.get('/business/websites');
    return response.data.data;
  },

  getWebsiteById: async (websiteId: string) => {
    const response = await apiClient.get(`/business/websites/${websiteId}`);
    return response.data.data;
  },

  updateWebsite: async (websiteId: string, data: UpdateWebsiteRequest) => {
    const response = await apiClient.put(`/business/websites/${websiteId}`, data);
    return response.data.data;
  },

  deleteWebsite: async (websiteId: string) => {
    const response = await apiClient.delete(`/business/websites/${websiteId}`);
    return response.data;
  },

  // Generates a brand logo via AI (using the business name/category/theme colors) and saves it.
  generateLogo: async (websiteId: string): Promise<{ logo: string }> => {
    const response = await apiClient.post(`/business/websites/${websiteId}/generate-logo`);
    return response.data.data;
  },

  getSections: async (websiteId: string) => {
    const response = await apiClient.get(`/business/websites/${websiteId}/sections`);
    return response.data.data;
  },

  updateSection: async (websiteId: string, sectionId: string, data: any) => {
    const response = await apiClient.put(`/business/websites/${websiteId}/sections/${sectionId}`, data);
    return response.data.data;
  },

  updateSectionContent: async (websiteId: string, sectionId: string, content: any) => {
    const response = await apiClient.put(`/business/content/${websiteId}/section/${sectionId}`, content);
    return response.data.data;
  },

  // Get content for any section (auto-detects single vs multi-record)
  getSectionContent: async (websiteId: string, sectionId: string) => {
    const response = await apiClient.get(`/business/content/${websiteId}/section/${sectionId}`);
    return response.data.data;
  },

  // Get content for single-record section
  getSingleContent: async (websiteId: string, sectionId: string) => {
    const response = await apiClient.get(`/business/content/${websiteId}/section/${sectionId}/single`);
    return response.data.data;
  },

  // Get items for multi-record section
  getContentItems: async (websiteId: string, sectionId: string) => {
    const response = await apiClient.get(`/business/content/${websiteId}/section/${sectionId}/items`);
    return response.data.data;
  },

  // Create item for multi-record section
  createContentItem: async (websiteId: string, sectionId: string, itemData: any) => {
    const response = await apiClient.post(`/business/content/${websiteId}/section/${sectionId}/items`, itemData);
    return response.data.data;
  },

  // Update item for multi-record section
  updateContentItem: async (websiteId: string, sectionId: string, itemId: string, itemData: any) => {
    const response = await apiClient.put(`/business/content/${websiteId}/section/${sectionId}/items/${itemId}`, itemData);
    return response.data.data;
  },

  // Delete item for multi-record section
  deleteContentItem: async (websiteId: string, sectionId: string, itemId: string) => {
    const response = await apiClient.delete(`/business/content/${websiteId}/section/${sectionId}/items/${itemId}`);
    return response.data;
  },

  // Update single-record section content
  updateSingleContent: async (websiteId: string, sectionId: string, content: any) => {
    const response = await apiClient.put(`/business/content/${websiteId}/section/${sectionId}/single`, content);
    return response.data.data;
  },

  deleteSectionContent: async (websiteId: string, sectionId: string) => {
    const response = await apiClient.delete(`/business/content/${websiteId}/section/${sectionId}`);
    return response.data;
  },

  // Special endpoints for catalogue_with_categories
  getCategoriesForSection: async (websiteId: string, sectionId: string) => {
    const response = await apiClient.get(`/business/content/${websiteId}/section/${sectionId}/categories`);
    return response.data.data;
  },

  getProductsForCategory: async (websiteId: string, sectionId: string, categoryId?: string) => {
    const url = `/business/content/${websiteId}/section/${sectionId}/products`;
    const response = await apiClient.get(url, {
      params: categoryId ? { categoryId } : {},
    });
    return response.data.data;
  },

  createProductWithCategory: async (websiteId: string, sectionId: string, categoryId: string, productData: any) => {
    const response = await apiClient.post(
      `/business/content/${websiteId}/section/${sectionId}/category/${categoryId}/products`,
      productData
    );
    return response.data.data;
  },

  // Update a product (and optionally move it to a different category)
  updateProduct: async (
    websiteId: string,
    sectionId: string,
    productId: string,
    productData: any,
    categoryId?: string
  ) => {
    const response = await apiClient.put(
      `/business/content/${websiteId}/section/${sectionId}/products/${productId}`,
      categoryId ? { ...productData, categoryId } : productData
    );
    return response.data.data;
  },

  // Delete a product
  deleteProduct: async (websiteId: string, sectionId: string, productId: string) => {
    const response = await apiClient.delete(
      `/business/content/${websiteId}/section/${sectionId}/products/${productId}`
    );
    return response.data;
  },

  // Special endpoints for gallery_with_categories (categories reuse getCategoriesForSection/createContentItem/etc above)
  getGalleryImagesForCategory: async (websiteId: string, sectionId: string, categoryId?: string) => {
    const url = `/business/content/${websiteId}/section/${sectionId}/gallery-images`;
    const response = await apiClient.get(url, {
      params: categoryId ? { categoryId } : {},
    });
    return response.data.data;
  },

  createGalleryImageWithCategory: async (websiteId: string, sectionId: string, categoryId: string, imageData: any) => {
    const response = await apiClient.post(
      `/business/content/${websiteId}/section/${sectionId}/gallery-category/${categoryId}/images`,
      imageData
    );
    return response.data.data;
  },

  // Update a gallery image (and optionally move it to a different category)
  updateGalleryImage: async (
    websiteId: string,
    sectionId: string,
    imageId: string,
    imageData: any,
    categoryId?: string
  ) => {
    const response = await apiClient.put(
      `/business/content/${websiteId}/section/${sectionId}/gallery-images/${imageId}`,
      categoryId ? { ...imageData, categoryId } : imageData
    );
    return response.data.data;
  },

  // Delete a gallery image
  deleteGalleryImage: async (websiteId: string, sectionId: string, imageId: string) => {
    const response = await apiClient.delete(
      `/business/content/${websiteId}/section/${sectionId}/gallery-images/${imageId}`
    );
    return response.data;
  },

  // Add section to website
  addSection: async (websiteId: string, sectionType: string, prompt: string) => {
    const response = await apiClient.post(`/business/websites/${websiteId}/sections`, {
      sectionType,
      prompt,
    });
    return response.data.data;
  },

  // Create one or more new sections from a typed/spoken instruction, e.g. "add a pricing section".
  createSectionsFromPrompt: async (websiteId: string, prompt: string) => {
    const response = await apiClient.post(`/business/websites/${websiteId}/sections/from-prompt`, { prompt });
    return response.data.data;
  },

  // Edit an existing section's content from a typed/spoken instruction.
  editSectionWithPrompt: async (websiteId: string, sectionId: string, prompt: string) => {
    const response = await apiClient.post(`/business/websites/${websiteId}/sections/${sectionId}/edit-with-prompt`, { prompt });
    return response.data.data;
  },

  // Remove section from website
  removeSection: async (websiteId: string, sectionId: string) => {
    const response = await apiClient.delete(`/business/websites/${websiteId}/sections/${sectionId}`);
    return response.data.data;
  },

  reorderSections: async (websiteId: string, sections: { sectionId: string; order: number }[]) => {
    const response = await apiClient.post(`/business/websites/${websiteId}/sections/reorder`, { sections });
    return response.data;
  },

  getTheme: async (websiteId: string) => {
    const response = await apiClient.get(`/business/websites/${websiteId}/theme`);
    return response.data.data;
  },

  updateTheme: async (websiteId: string, tokens: any) => {
    const response = await apiClient.put(`/business/websites/${websiteId}/theme`, { tokens });
    return response.data.data;
  },

  getThemePresets: async (): Promise<ThemePreset[]> => {
    const response = await apiClient.get('/business/websites/theme-presets');
    return response.data.data;
  },

  applyThemePreset: async (websiteId: string, presetId: string) => {
    const response = await apiClient.post(`/business/websites/${websiteId}/theme/apply-preset`, { presetId });
    return response.data.data;
  },

  getEnquiries: async (
    websiteId: string,
    page: number = 1,
    limit: number = 20,
    dateRange?: { fromDate?: string; toDate?: string }
  ): Promise<EnquiriesResponse> => {
    const response = await apiClient.get(`/business/websites/${websiteId}/enquiries`, {
      params: { page, limit, fromDate: dateRange?.fromDate, toDate: dateRange?.toDate },
    });
    return response.data.data;
  },

  getAnalytics: async (
    websiteId: string,
    dateRange?: { fromDate?: string; toDate?: string }
  ): Promise<AnalyticsSummary> => {
    const response = await apiClient.get(`/business/websites/${websiteId}/analytics`, {
      params: { fromDate: dateRange?.fromDate, toDate: dateRange?.toDate },
    });
    return response.data.data;
  },

  markEnquiryRead: async (websiteId: string, enquiryId: string): Promise<Enquiry> => {
    const response = await apiClient.patch(`/business/websites/${websiteId}/enquiries/${enquiryId}/read`);
    return response.data.data;
  },

  deleteEnquiry: async (websiteId: string, enquiryId: string) => {
    const response = await apiClient.delete(`/business/websites/${websiteId}/enquiries/${enquiryId}`);
    return response.data;
  },

  getNewsletterSubscribers: async (
    websiteId: string,
    page: number = 1,
    limit: number = 20
  ): Promise<NewsletterSubscribersResponse> => {
    const response = await apiClient.get(`/business/websites/${websiteId}/newsletter-subscribers`, {
      params: { page, limit },
    });
    return response.data.data;
  },

  deleteNewsletterSubscriber: async (websiteId: string, subscriberId: string) => {
    const response = await apiClient.delete(`/business/websites/${websiteId}/newsletter-subscribers/${subscriberId}`);
    return response.data;
  },
};

export interface Enquiry {
  _id: string;
  websiteId: string;
  name: string;
  email: string;
  phone?: string;
  message: string;
  isRead: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AnalyticsSummary {
  totalViews: number;
  uniqueVisitors: number;
  totalEnquiries: number;
  unreadEnquiries: number;
  newsletterSubscribers: number;
  sectionCount: number;
  productCount: number;
  fromDate: string | null;
  toDate: string | null;
}

export interface EnquiriesResponse {
  items: Enquiry[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  unreadCount: number;
}

export interface NewsletterSubscriber {
  _id: string;
  websiteId: string;
  email: string;
  createdAt: string;
  updatedAt: string;
}

export interface NewsletterSubscribersResponse {
  items: NewsletterSubscriber[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface ThemeTokens {
  primaryColor: string;
  secondaryColor: string;
  accentColor?: string;
  backgroundColor: string;
  textColor: string;
  headingColor?: string;
  borderColor?: string;
  cardBackgroundColor?: string;
  mutedTextColor?: string;
  iconBackgroundColor?: string;
  iconForegroundColor?: string;
  badgeBackgroundColor?: string;
  badgeTextColor?: string;
  inputBackgroundColor?: string;
  borderRadius: string;
  fontFamily: string;
  headingFontFamily?: string;
  fontSize: { base: string; small: string; large: string; h1: string; h2: string; h3: string };
  spacing: { xs: string; sm: string; md: string; lg: string; xl: string };
  padding: { section: string; card: string };
  shadow?: string;
  design?: {
    transition?: string;
    heroGradient?: string;
    buttonGradient?: string;
    cardGradient?: string;
    borderWidth?: string;
    cardShadow?: string;
    buttonShadow?: string;
    gap?: string;
    sectionGap?: string;
    sectionAnimation?: string;
    animationDuration?: string;
    hoverScale?: string;
    sectionBackgroundPattern?: 'none' | 'dots' | 'diagonal-lines' | 'blob' | 'grid' | 'circles' | 'waves';
  };
}

export interface ThemePreset {
  id: string;
  name: string;
  description: string;
  tokens: ThemeTokens;
}

export const publicApi = {
  getWebsiteBySlug: async (slug: string) => {
    const response = await apiClient.get(`/public/website/${slug}`);
    return response.data.data;
  },

  getLayouts: async (sectionType?: string) => {
    const response = await apiClient.get('/public/layouts', {
      params: sectionType ? { sectionType } : {},
    });
    return response.data.data;
  },
};
